import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Form, message } from 'antd';
import { useGroupCredentialingEnrollment } from 'hooks/credentialing/useGroupCredentialingEnrollment';
import { npiIssue, npiIssueText } from 'utils/npiCheck';
import { getGroupCredentialingEnrollment, saveGroupCredentialingEnrollment } from 'api/credentialing';
import PracticeEnrollmentFields from 'pages/GroupCredentialingPage/components/PracticeEnrollmentFields';
import {
    ENTITY_TYPE_OTHER,
    decodeEnrollmentEntityType,
    encodeEnrollmentEntityType,
} from 'pages/GroupCredentialingPage/constants';
import type { PracticeEnrollment } from 'types/credentialing';
import ReviewSection from '../ReviewSection';
import s from '../../s.module.less';

export interface PracticeEnrollmentSectionHandle {
    // group review 提交校验不通过时强制展开编辑（已在编辑中则不重置表单）
    openEdit: () => void;
    // 展开后触发字段校验标红（字段挂载后调用）
    validate: () => void;
}

interface Props {
    // 提交尝试后必填缺失时的红框/Attention badge（group review 用；Settings 弹窗不传）
    hasErrors?: boolean;
    // 宿主页面有 77px 固定底部导航时传 true（review 页）；弹窗内不传
    footerOffset?: boolean;
    // Settings 弹窗传 true：编辑态 Cancel/Save 在吸顶标题行（review 页不传，保持吸底条）
    actionsInHeader?: boolean;
    // 保存成功后的额外回调（如刷新外部校验状态）
    onSaved?: () => void;
    // 内联编辑态变化上报（group review 用它在编辑中禁用 Submit）
    onEditingChange?: (editing: boolean) => void;
}

const enrollmentItem = (label: string, value?: string) => (
    <div className={s.reviewItem} key={label}>
        <div className={s.reviewItemLabel}>{label}</div>
        <div className={value ? s.reviewItemValue : s.reviewItemEmpty}>{value || 'Not provided'}</div>
    </div>
);

// Practice enrollment（group 层，practice 只有一份）的 review 板块：只读六行 + 行内编辑，
// group review 页与 Settings 弹窗的 Practice enrollment tab 共用；交互经 ReviewSection 与其他 panel 一致。
const PracticeEnrollmentSection = forwardRef<PracticeEnrollmentSectionHandle, Props>(
    ({ hasErrors, footerOffset, actionsInHeader, onSaved, onEditingChange }, ref) => {
        const [enrollmentForm] = Form.useForm<PracticeEnrollment>();
        const [enrollment, , refetchEnrollment] = useGroupCredentialingEnrollment();
        const [editing, setEditing] = useState(false);

        // 编辑态所有进出口（openEdit / Cancel / 保存成功）统一上报
        useEffect(() => {
            onEditingChange?.(editing);
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [editing]);
        const [saving, setSaving] = useState(false);

        // Seeds its own form from the fetched enrollment, decoding the entityType/entityTypeOther pair.
        // /group/enrollment 是整份覆盖（没传的 billing 字段会被清空），所以先把完整 detail 灌进表单
        // （w9FileUrl / practiceRequirements 等表单上没有的字段也一起），保存时 getFieldsValue(true) 整份带回
        const openEdit = () => {
            enrollmentForm.setFieldsValue({
                ...enrollment,
                legalBusinessName: enrollment?.legalBusinessName || '',
                groupNpi: enrollment?.groupNpi || '',
                taxId: enrollment?.taxId || '',
                ...decodeEnrollmentEntityType(enrollment?.entityType),
                practiceLocation: enrollment?.practiceLocation || '',
                w9FileName: enrollment?.w9FileName || '',
                // 清掉上一次编辑残留的 NPI 查询态：表单实例跨开关复用，否则取消编辑后重开，
                // 旧 NPI 的查询结果会套在刚回填的 groupNpi 上。清空后 NpiRegistryBox 挂载会重新查
                npiType: undefined,
                npiChecking: undefined,
                npiRegistryName: undefined,
            });
            setEditing(true);
        };

        useImperativeHandle(ref, () => ({
            openEdit: () => {
                if (!editing) openEdit();
            },
            validate: () => {
                enrollmentForm.validateFields().catch(() => {});
            },
        }));

        // Clear the free-text field when the entity type moves off "Other", mirroring PracticeSetupStep.
        const handleValuesChange = (changed: Partial<PracticeEnrollment>) => {
            if ('entityType' in changed && changed.entityType !== ENTITY_TYPE_OTHER) {
                enrollmentForm.setFieldValue('entityTypeOther', '');
            }
        };

        const save = async () => {
            try {
                await enrollmentForm.validateFields();
            } catch {
                return; // invalid NPI/EIN — antd surfaces the field errors, keep the form open
            }
            // NPI Registry 校验不是 antd rule（查询态由 NpiRegistryBox 异步写进表单辅助字段），
            // validateFields 抓不到，必须单独拦一次，否则查不到/类型不对也能 Save changes
            const npiState = npiIssue(enrollmentForm.getFieldsValue(true), {
                npiKey: 'groupNpi',
                expectType: 'Type 2',
            });
            if (npiState) {
                message.error(npiIssueText(npiState, 'Type 2'));
                return;
            }
            setSaving(true);
            try {
                // 整份覆盖：先拉最新 detail 打底（同页 Medicaid group enrollment 板块可能刚改过），
                // practiceRequirements 传 null 让后端保留原值，这个板块不碰它
                let latest: any = {};
                try {
                    const fresh: any = await getGroupCredentialingEnrollment();
                    latest = fresh?.data?.data || {};
                } catch (e) {
                    // ignore：拉不到用表单里已有的完整数据
                }
                const res = await saveGroupCredentialingEnrollment(
                    encodeEnrollmentEntityType({
                        ...latest,
                        ...enrollmentForm.getFieldsValue(true),
                        practiceRequirements: null,
                    }),
                );
                if (res && !res.error) {
                    await refetchEnrollment();
                    setEditing(false);
                    onSaved?.();
                } else {
                    message.error(res?.error || 'Failed to save practice enrollment');
                }
            } finally {
                setSaving(false);
            }
        };

        return (
            <ReviewSection
                sectionKey="practice-enrollment"
                title="Practice enrollment"
                editing={editing}
                saving={saving}
                hasErrors={hasErrors}
                footerOffset={footerOffset}
                actionsInHeader={actionsInHeader}
                onEdit={openEdit}
                onCancel={() => setEditing(false)}
                onSave={save}
                viewContent={
                    <div className={s.reviewRow}>
                        {enrollmentItem('Legal business name', enrollment?.legalBusinessName)}
                        {enrollmentItem('Group NPI (Type 2)', enrollment?.groupNpi)}
                        {enrollmentItem('Tax ID / EIN', enrollment?.taxId)}
                        {enrollmentItem('Entity type', enrollment?.entityType)}
                        {enrollmentItem('Practice location', enrollment?.practiceLocation)}
                        {enrollmentItem('W-9 (group Tax ID)', enrollment?.w9FileName)}
                    </div>
                }
            >
                <Form
                    form={enrollmentForm}
                    layout="vertical"
                    requiredMark={false}
                    component={false}
                    onValuesChange={handleValuesChange}
                >
                    <PracticeEnrollmentFields form={enrollmentForm} required />
                </Form>
            </ReviewSection>
        );
    },
);

export default PracticeEnrollmentSection;
